"use client";

import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { ComponentMap } from "./component-map";

/* Generated by Copilot- Start */

/**
 * Predefined spacer configurations for common layout patterns
 */
const SPACER_CONFIGS = {
  afterHero: {
    mobile: "100px 0px",
    medium: "120px 0px"
  },
  afterPlanCards: {
    mobile: "100px 0px",
    medium: "200px 0px"
  },
  betweenContentCards: {
    mobile: "20px 0px",
    medium: "20px 0px"
  }
} as const;

/**
 * Component types that should not have content-visibility optimization.
 * The canvas now decides eligibility with the Pages Router position rule (WAT-12433), so
 * a top breadcrumb is never eligible anyway. BREADCRUMBSV2 stays listed as a deliberate
 * App-Router-only exemption: it also covers callers that omit contentVisibilityEligible,
 * and a breadcrumb lower on the page (where Pages would apply "auto") is harmless to skip.
 */
const NO_CONTENT_VISIBILITY_TYPES = ["SCROLLTOTOP", "RSNLOOKUPTOOL", "RSNFEELOOKUP", "PLANCARD", "BREADCRUMBSV2"] as const;

/**
 * Props interface for ComponentClientWrapper
 */
export interface ComponentClientWrapperProps {
  /** Type identifier for the component to render from ComponentMap */
  type: string;
  /** Props to pass to the rendered component */
  componentProps?: any;
  /** Unique key for React reconciliation (renamed from 'key' to avoid React reserved prop) */
  componentKey?: string | number;
  /** Whether this component is above the fold (affects rendering optimization) */
  aboveTheFold?: boolean;
  /** CMS type identifier (e.g., 'sb' for Storyblok) */
  cms_type?: string;
  /**
   * Whether the section is far enough down the page for content-visibility (Pages Router
   * rule, decided by the canvas). Defaults to !aboveTheFold when omitted.
   */
  contentVisibilityEligible?: boolean;
}

/**
 * Generates unique identifiers for section elements
 */
function generateSectionIds(componentProps: any, componentKey: string | number) {
  const isVerticalSpacer = componentProps?.type === "VERTICALSPACER";

  return {
    id: isVerticalSpacer ?
      `dtv2-VerticalSpacer${componentKey}` :
      `dtv2-${componentProps?.options?.id}`,
    dataSection: isVerticalSpacer ?
      `VerticalSpacer${componentKey}` :
      `${componentProps?.options?.id}`
  };
}

/**
 * Builds content-visibility style for performance optimization
 */
export function getContentVisibilityStyle(aboveTheFold: boolean, type: string) {
  if (aboveTheFold || NO_CONTENT_VISIBILITY_TYPES.includes(type as any)) {
    return {};
  }

  return {
    contentVisibility: type === "FLOATINGWIDGETV2" ? ("inherit" as const) : ("auto" as const)
  };
}

/**
 * Builds spacing styles based on spacer config or parentSpacers
 */
function getSpacingStyles(componentProps: any) {
  const { spacer, parentSpacers } = componentProps || {};

  // If using predefined spacer config
  if (spacer) {
    const spacerConfig = SPACER_CONFIGS[spacer as keyof typeof SPACER_CONFIGS];
    return {
      margin: {
        xs: spacer !== "" && spacerConfig ? spacerConfig.mobile : "0px",
        md: spacer !== "" && spacerConfig ? spacerConfig.medium : "0px"
      }
    };
  }

  // If using custom parent spacers
  if (parentSpacers) {
    return {
      marginTop: {
        xs: parentSpacers.mobileSpace || "20px",
        sm: parentSpacers.tabletSpace || "32px",
        md: parentSpacers.mediumSpace || "100px",
        lg: parentSpacers.largerSpace || "140px",
        xl: parentSpacers.extraLargeSpace || "180px"
      },
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0
    };
  }

  // Default: no margins
  return {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0
  };
}

/**
 * Builds sticky positioning styles if enabled
 */
function getStickyStyles(componentProps: any) {
  const { disableSticky } = componentProps || {};

  if (disableSticky !== undefined && !disableSticky) {
    return {
      position: {
        xs: "sticky" as const,
        md: "inherit" as const
      },
      zIndex: {
        xs: 1100,
        md: "auto" as const
      },
      top: {
        xs: 0,
        md: "auto" as const
      }
    };
  }

  return {};
}

/**
 * Client-side wrapper component that renders components from ComponentMap with
 * optimized styling, spacing, and performance features.
 */
export function ComponentClientWrapper({
  type,
  componentProps = {},
  componentKey = 0,
  aboveTheFold = false,
  cms_type,
  contentVisibilityEligible = !aboveTheFold
}: ComponentClientWrapperProps) {
  // Look up component from map with type safety
  const WnCComponent = ComponentMap[type as keyof typeof ComponentMap] as
    | React.ComponentType<any>
    | undefined;

  // Early return if component not found
  if (!WnCComponent) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`Component of type "${type}" not found in ComponentMap.`);
    }
    return null;
  }

  // Dev-only logging
  if (process.env.NODE_ENV !== "production") {
    console.debug(
      `[ComponentClientWrapper] Rendering type: ${type}, aboveTheFold: ${aboveTheFold}`
    );
  }

  // Generate section identifiers
  const { id, dataSection } = generateSectionIds(componentProps, componentKey);

  // Build combined styles
  const sectionStyle = getContentVisibilityStyle(!contentVisibilityEligible, type);
  const spacingStyles = getSpacingStyles(componentProps);
  const stickyStyles = getStickyStyles(componentProps);

  // WAT-11617: these are RESPONSIVE sx objects ({ xs, md, ... }) — the Pages
  // Router canvas resolves them via <DtvBox sx>. Passing them to a native
  // <div style> silently dropped every breakpoint value (React rejects object
  // CSS values), so App Router pages lost all spacer margins (plan card /
  // section header / FAQ spacing regressions) and sticky positioning.
  const boxSx = {
    ...spacingStyles,
    ...stickyStyles
  };

  // Build editable props for Storyblok CMS
  const editableProps = cms_type === "sb" ? componentProps.editableProps : undefined;

  return (
    <section
      data-section={dataSection}
      id={id || `component${componentKey}`}
      key={`component${componentKey}`}
      style={sectionStyle}
    >
      <DtvBox sx={boxSx} {...editableProps}>
        <WnCComponent {...componentProps} key={componentKey} />
      </DtvBox>
    </section>
  );
}

/* Generated by Copilot- End */
